import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { FaLinkedin, FaGithub, FaArrowDown } from "react-icons/fa";
import { SiLeetcode } from "react-icons/si";
import { HiOutlineDocumentArrowDown } from "react-icons/hi2";
import myAvatar from "../resources/Shivansh_avatar.png";

const useTypingEffect = (text, speed = 40) => {
  const [displayed, setDisplayed] = useState("");
  const [done, setDone] = useState(false);
  useEffect(() => {
    let i = 0;
    const interval = setInterval(() => {
      setDisplayed(text.slice(0, i + 1));
      i++;
      if (i >= text.length) { clearInterval(interval); setDone(true); }
    }, speed);
    return () => clearInterval(interval);
  }, [text, speed]);
  return { displayed, done };
};

const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.12 } } };
const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] } } };

const About = () => {
  const terminalText = "shivansh@dev:~$ cat about.json";
  const { displayed, done } = useTypingEffect(terminalText, 50);
  const [showContent, setShowContent] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (done) {
      const timer = setTimeout(() => setShowContent(true), 400);
      return () => clearTimeout(timer);
    }
  }, [done]);

  return (
    <div ref={containerRef} className="relative text-white overflow-hidden min-h-[90vh] flex flex-col justify-center">
      {/* Noise texture overlay */}
      <div className="noise-overlay"></div>

      {/* Gradient mesh background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] bg-blue-600/8 rounded-full blur-[120px] animate-drift"></div>
        <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] bg-purple-600/6 rounded-full blur-[150px] animate-drift-reverse"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] bg-indigo-500/5 rounded-full blur-[100px] animate-breathe"></div>
      </div>

      {/* Grid lines background */}
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }}></div>

      <div className="relative z-10 max-w-5xl mx-auto px-6 w-full">
        {/* Terminal header */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mb-10">
          <div className="inline-flex items-center bg-[#0d1117] border border-gray-800/80 rounded-t-lg px-4 py-2 gap-2">
            <span className="w-3 h-3 rounded-full bg-red-500/80"></span>
            <span className="w-3 h-3 rounded-full bg-yellow-500/80"></span>
            <span className="w-3 h-3 rounded-full bg-green-500/80"></span>
            <span className="ml-3 text-xs text-gray-600 font-mono">terminal</span>
          </div>
          <div className="bg-[#0d1117] border border-gray-800/80 border-t-0 rounded-b-lg rounded-tr-lg p-5">
            <p className="font-mono text-sm text-green-400">
              {displayed}<span className={`inline-block w-2 h-4 bg-green-400 ml-0.5 ${done ? 'animate-blink' : ''}`}></span>
            </p>
            {done && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="mt-3 font-mono text-xs text-gray-600">
                {"// loading response..."}
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Main content - appears after terminal typing */}
        {showContent && (
          <motion.div variants={stagger} initial="hidden" animate="visible">
            {/* Name + Avatar row */}
            <motion.div variants={fadeUp} className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                    <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"></span>
                    available for hire
                  </span>
                  <span className="text-gray-700 text-xs font-mono">v2.0.26</span>
                </div>
                <h1 className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tighter leading-[0.9]">
                  <span className="block text-white">Shivansh</span>
                  <span className="block bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-violet-400 to-purple-500">Gaur</span>
                </h1>
              </div>
              <div className="relative group">
                <div className="w-28 h-28 md:w-32 md:h-32 rounded-2xl overflow-hidden border-2 border-gray-800 group-hover:border-blue-500/40 transition-all duration-700 group-hover:rotate-3 group-hover:scale-105">
                  <img src={myAvatar} alt="Shivansh Gaur" className="w-full h-full object-cover scale-125 group-hover:scale-110 transition-transform duration-700" loading="lazy" />
                </div>
                <div className="absolute -inset-2 rounded-2xl bg-gradient-to-r from-blue-500/20 to-purple-500/20 blur-xl -z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
              </div>
            </motion.div>

            {/* Role */}
            <motion.div variants={fadeUp} className="mb-8">
              <p className="text-lg md:text-xl text-gray-400 font-light">
                Software Development Engineer at <span className="text-white font-medium hover-glow">Pathlock</span>
              </p>
              <p className="text-lg md:text-xl text-gray-500 font-light mt-1">
                PEC Chandigarh '25
              </p>
            </motion.div>

            {/* What I do — editorial style */}
            <motion.div variants={fadeUp} className="mb-12 max-w-2xl">
              <p className="text-gray-400 text-base md:text-lg leading-[1.8] font-light">
                I build <span className="text-white font-medium">event-driven microservices</span> and{" "}
                <span className="text-white font-medium">distributed systems</span> that handle millions of events.
                My code ships with <span className="text-white font-medium">circuit breakers</span>,{" "}
                <span className="text-white font-medium">message queues</span>, and{" "}
                <span className="text-white font-medium">ACID guarantees</span> — not excuses.
              </p>
            </motion.div>

            {/* Stats — large editorial numbers */}
            <motion.div variants={fadeUp} className="grid grid-cols-2 md:grid-cols-4 gap-px bg-gray-800/30 rounded-xl overflow-hidden mb-12">
              {[
                { num: "1000+", label: "Problems Crushed", sub: "LeetCode" },
                { num: "1950+", label: "Peak Rating", sub: "Knight Badge" },
                { num: "4500+", label: "Lines Shipped", sub: "Production" },
                { num: "Top 5%", label: "Global Rank", sub: "Worldwide" },
              ].map((s, i) => (
                <div key={i} className="group bg-[#0a0a2e]/60 backdrop-blur-sm p-6 text-center hover:bg-gray-800/40 transition-colors duration-500 cursor-default">
                  <div className="text-3xl md:text-4xl font-black text-white group-hover:text-blue-400 transition-colors duration-500 tabular-nums">{s.num}</div>
                  <div className="text-xs text-gray-500 mt-1 uppercase tracking-[0.2em] font-medium">{s.label}</div>
                  <div className="text-[10px] text-gray-700 mt-0.5 font-mono">{s.sub}</div>
                </div>
              ))}
            </motion.div>

            {/* Tech stack marquee */}
            <motion.div variants={fadeUp} className="mb-12 overflow-hidden">
              <div className="text-[10px] text-gray-700 uppercase tracking-[0.3em] mb-3 font-mono">tech stack</div>
              <div className="flex gap-3 animate-marquee">
                {["C#", ".NET Core", "Python", "FastAPI", "React", "TypeScript", "RabbitMQ", "Redis", "Docker", "AWS", "MongoDB", "SQL Server", "WebSockets", "EF Core", "DynamoDB"].map((t, i) => (
                  <span key={i} className="flex-shrink-0 px-3 py-1.5 text-xs font-mono text-gray-500 border border-gray-800/60 rounded-md hover:text-blue-400 hover:border-blue-500/30 transition-all duration-300 cursor-default whitespace-nowrap">{t}</span>
                ))}
                {["C#", ".NET Core", "Python", "FastAPI", "React", "TypeScript", "RabbitMQ", "Redis", "Docker", "AWS", "MongoDB", "SQL Server", "WebSockets", "EF Core", "DynamoDB"].map((t, i) => (
                  <span key={`dup-${i}`} className="flex-shrink-0 px-3 py-1.5 text-xs font-mono text-gray-500 border border-gray-800/60 rounded-md hover:text-blue-400 hover:border-blue-500/30 transition-all duration-300 cursor-default whitespace-nowrap">{t}</span>
                ))}
              </div>
            </motion.div>

            {/* Links — minimal, horizontal */}
            <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-6">
              <a href="https://drive.google.com/drive/folders/1FMDm73GTPLvnnk_X1qOF7mEnODblZC0G" target="_blank" rel="noopener noreferrer"
                className="group inline-flex items-center gap-2 text-sm font-medium text-white hover:text-blue-400 transition-colors duration-300">
                <HiOutlineDocumentArrowDown className="text-lg" />
                <span className="border-b border-gray-700 group-hover:border-blue-400/50 pb-0.5 transition-colors">Resume</span>
              </a>
              <a href="https://github.com/Shivansh-Gaur2/" target="_blank" rel="noopener noreferrer"
                className="group inline-flex items-center gap-2 text-sm font-medium text-gray-400 hover:text-white transition-colors duration-300">
                <FaGithub className="text-lg" />
                <span className="border-b border-transparent group-hover:border-gray-600 pb-0.5 transition-colors">GitHub</span>
              </a>
              <a href="https://www.linkedin.com/in/shivansh-gaur/" target="_blank" rel="noopener noreferrer"
                className="group inline-flex items-center gap-2 text-sm font-medium text-gray-400 hover:text-white transition-colors duration-300">
                <FaLinkedin className="text-lg" />
                <span className="border-b border-transparent group-hover:border-gray-600 pb-0.5 transition-colors">LinkedIn</span>
              </a>
              <a href="https://leetcode.com/u/shivanshgaur28/" target="_blank" rel="noopener noreferrer"
                className="group inline-flex items-center gap-2 text-sm font-medium text-gray-400 hover:text-yellow-500 transition-colors duration-300">
                <SiLeetcode className="text-lg" />
                <span className="border-b border-transparent group-hover:border-yellow-500/50 pb-0.5 transition-colors">LeetCode</span>
              </a>
            </motion.div>
          </motion.div>
        )}
      </div>

      {/* Scroll hint */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 0.3 }} transition={{ delay: 4 }} className="absolute bottom-8 left-1/2 -translate-x-1/2">
        <FaArrowDown className="text-sm animate-bounce" />
      </motion.div>
    </div>
  );
};

export default About;
